import React, { useState } from 'react';
import {
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  TablePagination,
  TableSortLabel,
  LinearProgress,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { CircularProgress, Typography } from '@mui/material';
import moment from 'moment';

import { showErrorAction } from 'containers/Notification/actions';
import { PureRender } from 'utils/component-helpers';
import { TableBox } from 'retired/TableBox';
import {
  useNonprofitNominationsQuery,
  useNominationsCountQuery,
  NonprofitNomination,
  useNonprofitNominationsLazyQuery,
} from 'generated/graphql';
import TablePaginationActions from 'retired/TablePaginationActions';
import User from 'entities/user/model';
import { Privileges } from 'utils/privileges';
import { downloadFileFromJson } from 'utils/downloadFileFromJson';
import { primaryBlue } from 'utils/color-styles';
import { formatFullName } from 'utils/formatFullName';

import { COLUMNS, HeaderColumnInterface } from './constants';
import NonprofitNominationsRow from './NonprofitNominationsRow';
import { getLastOutreachDate } from './getLastOutreachDate';

const displayDate = (date?: Date | null): string =>
  date ? moment(date).format('MMM DD, YYYY') : '-';

const NonprofitNominationsTable = ({
  user,
  viewAllCompanies,
}: {
  user: User;
  viewAllCompanies: boolean;
}): JSX.Element => {
  const dispatch = useDispatch();

  const hasGlobalHandleNonprofitNominationsPrivilege = user.hasPrivilege(
    Privileges.DEED_INTERNAL_HANDLE_NONPROFIT_NOMINATIONS
  );

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);

  const [fetchSortLabel, setFetchSortLabel] = useState<{
    sortedBy: keyof NonprofitNomination | 'nominator';
    order: 'asc' | 'desc';
  }>({
    sortedBy: 'createdAt',
    order: 'desc',
  });

  const [sortLabel, setSortLabel] = useState<{
    sortedBy: keyof NonprofitNomination | 'nominator';
    order: 'asc' | 'desc';
  }>({
    sortedBy: fetchSortLabel.sortedBy,
    order: fetchSortLabel.order,
  });

  const localeCode = new Intl.DisplayNames(['en'], {
    type: 'region',
  });

  const goToPage = (__: unknown, pageNum: number): void => {
    let pageValue = 0;
    if (pageNum * limit > 0) {
      pageValue = pageNum * limit;
    }
    setOffset(pageValue);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setLimit(Number(event.target.value));
  };

  const getSorted = (
    sortedBy: keyof NonprofitNomination | 'nominator',
    order: 'asc' | 'desc',
    unSorted: NonprofitNomination[]
  ): NonprofitNomination[] => {
    if (sortedBy === 'nominator') {
      return unSorted
        .slice()
        .sort((a, b) =>
          order === 'asc'
            ? a.User.fullName.last.localeCompare(b.User.fullName.last)
            : b.User.fullName.last.localeCompare(a.User.fullName.last)
        );
    }
    if (sortedBy === 'Company') {
      return unSorted
        .slice()
        .sort((a, b) =>
          order === 'asc'
            ? a.Company.name.localeCompare(b.Company.name)
            : b.Company.name.localeCompare(a.Company.name)
        );
    }
    if (sortedBy === 'country') {
      return unSorted.slice().sort((a, b) => {
        const countryA = localeCode.of(a.country.substring(0, 2));
        const countryB = localeCode.of(b.country.substring(0, 2));
        return order === 'asc'
          ? countryA.localeCompare(countryB)
          : countryB.localeCompare(countryA);
      });
    }

    if (sortedBy === 'emails') {
      return unSorted.slice().sort((a, b) => {
        const aLastOutreachDate = getLastOutreachDate(a);
        const bLastOutreachDate = getLastOutreachDate(b);

        if (!aLastOutreachDate && !bLastOutreachDate) {
          return 0;
        }
        if (!aLastOutreachDate) {
          return order === 'asc' ? -1 : 1;
        }
        if (!bLastOutreachDate) {
          return order === 'asc' ? 1 : -1;
        }

        const aTime = new Date(aLastOutreachDate).getTime();
        const bTime = new Date(bLastOutreachDate).getTime();

        return order === 'asc' ? aTime - bTime : bTime - aTime;
      });
    }

    return unSorted.slice().sort((a, b) => {
      const aValue = a[sortedBy] ?? '';
      const bValue = b[sortedBy] ?? '';

      if (order === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  const onSortedLabelClick = (item: HeaderColumnInterface): void => {
    if (item.id === sortLabel.sortedBy) {
      const order = sortLabel.order === 'desc' ? 'asc' : 'desc';
      setSortLabel({
        sortedBy: item.id,
        order,
      });
    } else {
      setSortLabel({
        sortedBy: item.id,
        order: 'desc',
      });
    }
  };

  const { data: count } = useNominationsCountQuery({
    variables: {
      companyId: viewAllCompanies ? null : user?.organization.id,
    },
  });

  const commonVariables = {
    companyId: viewAllCompanies ? null : user.organization.id,
    sortedBy: fetchSortLabel.sortedBy,
    order: fetchSortLabel.order,
  };

  const { data, loading } = useNonprofitNominationsQuery({
    variables: {
      ...commonVariables,
      limit,
      offset,
    },
    onError: () => {
      dispatch(showErrorAction('There was an error loading nonprofit nominations'));
    },
  });
  const [fetchNonprofitNomitaions, { loading: csvDownloading }] = useNonprofitNominationsLazyQuery({
    variables: {
      ...commonVariables,
      offset: 0,
    },
    onError: () => {
      dispatch(showErrorAction('There was an error exporting nonprofit nominations'));
    },
  });

  if (loading) {
    return <LinearProgress />;
  }

  const exportButton =
    user.isAdmin &&
    (csvDownloading ? (
      <CircularProgress size={16} />
    ) : (
      <Typography
        variant="button"
        style={{ cursor: 'pointer', color: primaryBlue }}
        title={`Export all nonprofit nominations ${
          viewAllCompanies ? 'for all companies' : 'for the current company'
        }`}
        onClick={async () => {
          let records = data?.nonprofitNominations;
          // @NOTE-DP: if there are more records in total then the current page limit, then we need to fetch all the records in a separte request
          if ((count?.nominationsCount || 0) > limit) {
            const result = await fetchNonprofitNomitaions();
            records = result.data?.nonprofitNominations;
          }

          if (records) {
            downloadFileFromJson(
              records.map((record) => ({
                id: record.id,
                nonprofit: record.nonprofitName,
                country: record.country,
                request: record.engagementType,
                email: record.email,
                ein: record.ein,
                nces: record.ncesIdentifier,
                submittedDate: displayDate(record.createdAt),
                nominator: formatFullName(record.User?.fullName),
                company: record.Company?.name,
                status: record.status,
                otherNote: record.otherNote,
                internalNote: record.internalNote,
              })),
              `nonprofit_nominations_${viewAllCompanies ? 'all' : user.organization.id}.csv`
            );
          }
        }}
      >
        Export All
      </Typography>
    ));

  return (
    <TableBox
      fixedBottom={
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                color: '#0000008a',
                fontSize: '0.875rem',
              }}
            >
              Total: {count?.nominationsCount}
            </div>
            {exportButton}
          </div>
          <TablePagination
            component="div"
            count={count?.nominationsCount || 0}
            rowsPerPage={limit}
            page={offset / limit || 0}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onChangePage={(__, page): void => {
              goToPage(__, page);
              setFetchSortLabel(sortLabel);
            }}
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
              native: true,
            }}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        </>
      }
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.filter((column) =>
              hasGlobalHandleNonprofitNominationsPrivilege ? true : column.admin === false
            ).map((item, i) => (
              <TableCell
                key={item.id}
                size="medium"
                style={
                  i === 0
                    ? {
                        position: 'sticky',
                        zIndex: 1,
                        left: 0,
                        backgroundColor: 'white',
                      }
                    : {}
                }
              >
                {item.sorted ? (
                  <TableSortLabel
                    direction={sortLabel.order}
                    active={sortLabel.sortedBy === item.id}
                    onClick={(): void => {
                      onSortedLabelClick(item);
                    }}
                  >
                    {item.label}
                  </TableSortLabel>
                ) : (
                  item.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.nonprofitNominations &&
            getSorted(
              sortLabel.sortedBy,
              sortLabel.order,
              data.nonprofitNominations as NonprofitNomination[]
            ).map((nomination) => (
              <NonprofitNominationsRow
                nomination={nomination}
                hasGlobalHandleNonprofitNominationsPrivilege={
                  hasGlobalHandleNonprofitNominationsPrivilege
                }
                key={nomination.id}
              />
            ))}
        </TableBody>
      </Table>
    </TableBox>
  );
};

export default PureRender(NonprofitNominationsTable);
